import React, { useEffect, useRef, useState } from 'react';
import { Drawer } from 'antd';

const MIN_WIDTH_PCT = 30;
const DEFAULT_WIDTH_PCT = 50;
const MAX_WIDTH_PCT = 75;

/**
 * Right-side, resizable (30–75%, 50% default) drawer to create a new MCP API
 * key. Collapses to full width on small screens.
 */
export default function McpKeyCreateDrawer({ open, saving = false, error = '', onSubmit, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [widthPct, setWidthPct] = useState(DEFAULT_WIDTH_PCT);
  const [isMobile, setIsMobile] = useState(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    const onMove = (event) => {
      if (!draggingRef.current) return;
      const fromRight = window.innerWidth - event.clientX;
      const pct = Math.round((fromRight / window.innerWidth) * 100);
      setWidthPct(Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, pct)));
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const canSubmit = name.trim().length > 0 && !saving;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit?.({ name: name.trim(), description: description.trim() });
  };

  return (
    <Drawer
      open={open}
      placement="right"
      width={isMobile ? '100%' : `${widthPct}vw`}
      onClose={onCancel}
      maskClosable={!saving}
      keyboard={!saving}
      title="Create MCP API key"
      styles={{ body: { padding: 0 } }}
    >
      {!isMobile && (
        <div
          role="separator"
          aria-orientation="vertical"
          onMouseDown={() => {
            draggingRef.current = true;
            document.body.style.userSelect = 'none';
          }}
          style={{ position: 'absolute', top: 0, left: 0, width: 6, height: '100%', cursor: 'col-resize', zIndex: 10 }}
        />
      )}
      <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
        <div className="flex-grow-1 overflow-auto px-3 px-md-4 py-3">
          <p className="text-muted mb-3">
            Create a dedicated, revocable key for the MCP server. It is scoped to this account (tenant)
            and grants full OrderX access — use a separate key per integration.
          </p>
          {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
          <div className="mb-3">
            <label className="form-label mb-1" htmlFor="mcp-key-name">Name</label>
            <input
              id="mcp-key-name"
              type="text"
              className="form-control"
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Cursor MCP – analytics"
            />
          </div>
          <div className="mb-3">
            <label className="form-label mb-1" htmlFor="mcp-key-desc">Description (optional)</label>
            <textarea
              id="mcp-key-desc"
              className="form-control"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Where this key is used"
            />
          </div>
        </div>
        <div className="border-top px-3 px-md-4 py-3 d-flex justify-content-end gap-2 bg-white">
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {saving ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
